import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import MoreMenu from './MoreMenu';
import useGlobalToggle from '../../hooks/useGlobalToggle';
import styles from './HorizontalTabs.module.less';

/**
 * Create an array of width values from a DOM NodeList.
 *
 * @param {HTMLCollection} tabs
 * @returns {Array<number>}
 */
const getTabWidths = tabs => {
  return Array.from(tabs).map(tab => tab.offsetWidth);
};

/**
 * Classifies Tabs as either hidden or visible, depending on the width of each Tab, the width of the container,
 * and the currently selected Tab.
 *
 * @param {Array<Tab | ReactChild>} tabs
 * @param {Array<number>} tabWidths
 * @param {number} containerWidth
 * @param {number?} max
 * @returns {Array<Array<Tab>>}
 */
const getVisibleAndHiddenTabs = (tabs, tabWidths, containerWidth, max) => {
  // Width when all tabs are shown
  let currentWidth = tabWidths.reduce((a, b) => a + b, 0);

  // If tab widths haven't been calculated yet, show them all
  if (tabWidths.length === 0 || currentWidth <= containerWidth) {
    return [tabs, []];
  }

  // Allow enough space for the MoreMenu button
  currentWidth += 115;

  // Loop through and build array of hidden tabs
  // Start removing tabs from the end until it will fit in the container
  const hidden = [];
  for (let i = tabs.length - 1; i >= 0; i--) {
    // Finish if tabs width is smaller than container, or max tabs reached
    if (currentWidth <= containerWidth && (max == null || tabs.length <= max)) {
      break;
    }
    // Always show the selected tab
    if (tabs[i].props.selected) {
      continue;
    }

    // Move this tab to the hidden array, subtract the tab width, and keep going
    const tab = tabs.splice(i, 1)[0];
    hidden.unshift(React.cloneElement(tab, { inDropdown: true }));
    currentWidth -= tabWidths[i];
  }

  return [tabs, hidden];
};

const HorizontalTabs = ({ children, max = null }) => {
  /** @type {{current: ?HTMLElement}} **/
  const rootRef = useRef(null);
  /** @type {{current: ?HTMLElement}} **/
  const menuRef = useRef(null);

  const [open, setOpen] = useGlobalToggle(menuRef);
  const [containerWidth, setContainerWidth] = useState(0);
  const [tabWidths, setTabWidths] = useState([]);
  const [isReady, setIsReady] = useState(false);

  // Re-layout when window is re-sized
  useEffect(() => {
    const handleResize = () => {
      rootRef.current != null && setContainerWidth(rootRef.current.offsetWidth);
    };

    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Calculate tab withs when component mounts
  useEffect(() => {
    rootRef.current != null &&
      setTabWidths(getTabWidths(rootRef.current.children));
    setIsReady(true);
  }, [children.length]);

  const handleToggleMenu = () => {
    setOpen(!open);
  };

  const [visibleArray, hiddenArray] = getVisibleAndHiddenTabs(
    React.Children.toArray(children),
    tabWidths,
    containerWidth,
    max
  );

  return (
    <div
      className={clsx(styles.TabsHorizontal, isReady && styles.show)}
      ref={rootRef}
    >
      {visibleArray}
      {hiddenArray.length > 0 && (
        <MoreMenu
          ref={menuRef}
          isOpen={open}
          onClickToggle={handleToggleMenu}
          onClickItem={handleToggleMenu}
        >
          {hiddenArray}
        </MoreMenu>
      )}
    </div>
  );
};

HorizontalTabs.displayName = 'HorizontalTabs';
HorizontalTabs.propTypes = {
  children: PropTypes.node,
  /**
   * The maximum number of tabs to display.
   * Fewer tabs will be shown if there is not enough space to display max tabs.
   */
  max: PropTypes.number,
  clickHandler: PropTypes.func,
};

export default HorizontalTabs;
